import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Package, CreditCard, Truck, Loader2, Search } from "lucide-react";
import Seo from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { key: "awaiting_payment", label: "Order Placed", sub: "Waiting on your Zelle / Cash App", Icon: Package },
  { key: "paid", label: "Payment Received", sub: "Your pieces are in production", Icon: CreditCard },
  { key: "shipped", label: "Shipped", sub: "On its way to you", Icon: Truck },
];

export default function TrackOrder() {
  const [orderNumber, setOrderNumber] = useState("");
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const track = async () => {
    if (!orderNumber.trim()) return;
    setLoading(true);
    setError("");
    setOrder(null);
    try {
      const res = await axios.get(`${API}/orders/track/${encodeURIComponent(orderNumber.trim())}`);
      setOrder(res.data);
    } catch {
      setError("We couldn't find that order number. Check the confirmation email — it looks like AF-XXXXXX.");
    } finally {
      setLoading(false);
    }
  };

  const stepIndex = order ? STEPS.findIndex((s) => s.key === order.status) : -1;

  return (
    <main data-testid="track-page" className="min-h-screen flex items-center justify-center px-5 py-24">
      <Seo title="Track Your Order — Always & Forever" siteName="Always & Forever" description="Check the status of your Always & Forever order." />
      <div className="w-full max-w-lg">
        <Link to="/" data-testid="track-home-link" className="block text-center text-[10px] uppercase tracking-[0.3em] text-white/40 hover:text-white transition-colors mb-10">
          Always &amp; Forever
        </Link>

        <h1 className="font-display font-medium uppercase tracking-tight text-4xl sm:text-5xl text-center mb-3">Track Order</h1>
        <p className="text-sm text-white/50 font-light text-center mb-10">Enter the order number from your confirmation email.</p>

        <div className="flex gap-2 mb-12">
          <input
            data-testid="track-input"
            value={orderNumber}
            onChange={(e) => setOrderNumber(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && track()}
            placeholder="AF-XXXXXX"
            className="flex-1 bg-white/5 border border-white/15 rounded-full px-6 py-4 text-sm uppercase tracking-[0.15em] placeholder:text-white/25 focus:outline-none focus:border-[#ff1053]/70 transition-colors"
          />
          <button
            data-testid="track-submit-btn"
            onClick={track}
            disabled={loading}
            className="bg-white text-black rounded-full px-7 py-4 text-xs uppercase tracking-[0.22em] font-semibold flex items-center gap-2 transition-transform duration-300 hover:scale-105 active:scale-95 disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            Track
          </button>
        </div>

        {error && (
          <p data-testid="track-error" className="text-sm text-[#ff1053] text-center font-light">{error}</p>
        )}

        {order && (
          <div data-testid="track-result" className="glass-panel rounded-sm p-8">
            <div className="flex justify-between items-baseline mb-8">
              <p className="font-unbounded text-lg">{order.order_number}</p>
              <p className="font-unbounded text-sm text-[#ff1053]">${order.total.toFixed(2)}</p>
            </div>

            <div className="flex flex-col gap-0 mb-8">
              {STEPS.map((s, i) => {
                const reached = i <= stepIndex;
                const current = i === stepIndex;
                return (
                  <div key={s.key} data-testid={`track-step-${s.key}`} className="flex gap-4">
                    <div className="flex flex-col items-center">
                      <span className={`w-9 h-9 rounded-full border flex items-center justify-center transition-colors ${
                        reached ? "bg-[#ff1053] border-[#ff1053] text-white" : "border-white/20 text-white/30"
                      }`}>
                        <s.Icon className="w-4 h-4" />
                      </span>
                      {i < STEPS.length - 1 && <span className={`w-px h-8 ${i < stepIndex ? "bg-[#ff1053]" : "bg-white/15"}`} />}
                    </div>
                    <div className="pt-1.5 pb-6">
                      <p className={`text-sm font-medium ${reached ? "text-white" : "text-white/35"}`}>
                        {s.label}
                        {current && <span className="ml-2 text-[9px] uppercase tracking-[0.2em] text-[#ff1053]">You are here</span>}
                      </p>
                      <p className="text-xs text-white/40 font-light mt-0.5">{s.sub}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="border-t border-white/10 pt-5">
              <p className="text-[9px] uppercase tracking-[0.25em] text-white/40 mb-3">Your pieces</p>
              <ul className="flex flex-col gap-2">
                {order.items.map((it, i) => (
                  <li key={i} className="flex justify-between text-sm">
                    <span className="text-white/80">{it.name} <span className="text-white/40">· Size {it.size} × {it.quantity}</span></span>
                    <span className="text-white/60">${it.line_total.toFixed(2)}</span>
                  </li>
                ))}
              </ul>
              <p className="text-[10px] uppercase tracking-[0.2em] text-white/35 mt-4">
                Paying via {order.payment_method === "zelle" ? "Zelle" : "Cash App"}
              </p>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
